import React, { useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { motion } from "framer-motion";
import {
  Activity,
  ShieldCheck,
  ArrowRight,
  Orbit,
  Sparkles,
  Lock,
} from "lucide-react";

// Logo
import LogoImage from "../assets/logo.png";

const AuthPage = () => {
  const [loading, setLoading] = useState(false);

  /* -------------------------------
     GOOGLE LOGIN
  -------------------------------- */
  const loginWithGoogle = async () => {
    if (loading) return; // 🔒 prevent double click
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: { prompt: "select_account" },
        },
      });

      if (error) throw error;
    } catch (err) {
      console.error("Google OAuth error:", err);
      alert("Google sign-in failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#030405] px-6 relative overflow-hidden font-sans">
      {/* ---------- BACKGROUND ---------- */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald-500/5 rounded-full blur-[160px]" />
        <div
          className="absolute inset-0 opacity-[0.15] [mask-image:radial-gradient(circle,white,transparent_80%)]"
          style={{
            backgroundImage:
              "radial-gradient(#334155 0.5px, transparent 0.5px)",
            backgroundSize: "24px 24px",
          }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-[440px] z-10"
      >
        {/* ---------- BRAND ---------- */}
        <div className="flex flex-col items-center mb-10">
          <motion.div initial={{ y: -10 }} animate={{ y: 0 }} className="relative p-1 mb-8">
            <div className="absolute inset-0 bg-emerald-500/20 blur-2xl rounded-full animate-pulse" />
            <div className="relative w-24 h-24 rounded-[2rem] bg-gradient-to-b from-slate-800 to-slate-950 p-[2px] shadow-2xl">
              <div className="w-full h-full rounded-[1.9rem] bg-[#080a0c] flex items-center justify-center overflow-hidden">
                <img
                  src={LogoImage}
                  alt="Fitness Tracker Logo"
                  className="w-14 h-14 object-contain"
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://cdn-icons-png.flaticon.com/512/8437/8437151.png";
                  }}
                />
              </div>
            </div>
          </motion.div>

          <h1 className="text-4xl font-bold text-white tracking-tighter">
            Fitness<span className="text-emerald-500">Tracker</span>
          </h1>

          <div className="flex items-center gap-2 mt-2 opacity-60">
            <Orbit className="w-3 h-3 text-emerald-400 animate-spin-slow" />
            <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-slate-300">
              AI-Powered Fitness
            </span>
          </div>
        </div>

        {/* ---------- AUTH CARD ---------- */}
        <div className="relative overflow-hidden bg-slate-900/30 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] p-1 shadow-[0_0_80px_rgba(0,0,0,0.4)]">
          <div className="bg-[#0b0e12]/90 rounded-[2.4rem] p-8 sm:p-10">
            <div className="mb-10 space-y-2">
              <div className="flex items-center gap-2 text-emerald-400 font-bold text-[10px] uppercase tracking-widest">
                <Sparkles className="w-3 h-3" />
                <span>Next-Gen Analysis</span>
              </div>
              <h2 className="text-2xl font-semibold text-white tracking-tight">
                Welcome to the Lab
              </h2>
              <p className="text-slate-500 text-sm">
                Sync your biometrics and start your data-driven fitness journey.
              </p>
            </div>

            {/* ---------- GOOGLE BUTTON ---------- */}
            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              onClick={loginWithGoogle}
              disabled={loading}
              className="relative w-full h-16 bg-white rounded-2xl flex items-center justify-between px-8 font-black shadow-xl shadow-white/5 disabled:opacity-60"
            >
              {loading ? (
                <div className="w-full flex justify-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                    className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full"
                  />
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-4">
                    <img
                      src="https://www.svgrepo.com/show/475656/google-color.svg"
                      className="w-6 h-6"
                      alt="Google"
                    />
                    <span className="text-slate-900 text-lg uppercase">
                      Sync with Google
                    </span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400" />
                </>
              )}
            </motion.button>

            {/* ---------- FEATURES ---------- */}
            <div className="mt-10 pt-8 border-t border-white/5 grid grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 text-white font-bold text-sm italic">
                  <Activity className="w-3.5 h-3.5 text-emerald-500" />
                  Live-Track
                </div>
                <p className="text-[10px] text-slate-500 uppercase font-bold">
                  Heart-rate & Effort
                </p>
              </div>

              <div>
                <div className="flex items-center gap-2 text-white font-bold text-sm italic">
                  <Lock className="w-3.5 h-3.5 text-blue-500" />
                  Vault
                </div>
                <p className="text-[10px] text-slate-500 uppercase font-bold">
                  Secure Data-Silo
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ---------- FOOTER ---------- */}
        <footer className="mt-12 flex flex-col items-center gap-4">
          <div className="flex items-center gap-3 px-4 py-1.5 rounded-full bg-white/[0.03] border border-white/5">
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              End-to-End Encryption Enabled
            </span>
          </div>
          <p className="text-[10px] text-slate-600 font-medium">
            © 2026 Fitness Tracker. All rights reserved.
          </p>
        </footer>
      </motion.div>

      {/* ---------- LOCAL ANIMATION ---------- */}
      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default AuthPage;
